// ════════════════════════════════════════════════════════════════════
// THE NPC TOME BOOK — one row per Tome, and the row is the law
// ════════════════════════════════════════════════════════════════════
// WHY THIS FILE EXISTS, stated plainly so nobody re-grows the disease
// it was written to cure:
//
// The brain used to decide what it could do with a Tome by consulting
// FOUR hand-maintained id lists in NPC.js — NPC_KNOWN_TOME_EFFECTS,
// GOOD_TOMES, AGGRESSIVE_TOMES, SSG_TOMES — plus a two-entry blacklist,
// none of which knew about the others and none of which knew about the
// dispatcher they were supposed to describe. Four lists drift four
// ways. They did: AGGRESSIVE_TOMES carried "tome_omnis_corruption_jet",
// an id that has never existed in cards.js, so the one Tome that ruins
// an Impera or a U'hlti was unreachable on the aggressive lane for as
// long as anybody can remember. Nothing caught it, because nothing was
// ever asked to.
//
// THE FIX IS STRUCTURAL, NOT CLERICAL. This module holds exactly one
// row per Tome id, and it ASSERTS AT LOAD that the set of rows and the
// set of Tomes in cards.js are the same set. A Tome with no row throws.
// A row naming a Tome that does not exist throws. There is no third
// state and no place for a fifth list to hide. Every consumer in NPC.js
// reads this table and nothing else.
//
// ── THE SPINE: NEVER PAY FOR NOTHING ────────────────────────────────
// Every row carries a `want(V)` that returns a NUMBER when firing the
// Tome would change the board in the NPC's favour, and NULL when it
// would not. A Ritual Tome with an empty Necropolis is worth nothing.
// A ruin-a-Tome Tome against a bare board is worth nothing. Solomon
// Godfry with no adversary being is worth LESS than nothing — it is a
// forced sacrifice with no upside, and the brain used to fire it
// anyway. `want` returning null means the caller does not pay the fare,
// does not flip the card, and does not burn the rift.
//
// This is the same law the banner over `flipOneTome` has claimed to
// enforce since the Awakening. It was true for the seventeen Tomes the
// dispatcher knew and false for the other fifty-five: `npcTomeIsWorth-
// Paying` had an escape hatch for PERSISTENT_TOME_TAGS on the theory
// that a persistent Tome's value "is simply STANDING there". For a
// taunt wall that is true. For all nine Ritual Tomes — pure activated
// searches with zero standing value — it was flatly false, and the
// brain paid 9 Khao for Mausoleum of Omnis every two turns, forever,
// for nothing. Fifteen cards were doing that. Standing is now a LANE
// rather than a loose flag, and exactly EIGHT rows are on it.
//
// ⚠ THE COUNT WAS WRONG IN THIS BANNER AND IN NPC.js FOR AS LONG AS
// BOTH EXISTED — both said "eleven", and it was ten, and two of the ten
// were the contradiction described over row() below. A number written
// in prose beside a table is a number nobody re-counts, so
// tools/tomeLane.selftest.mjs now counts the lane and reads this
// sentence back. Change the table and the sentence fails until it
// agrees.
//
// ── DECLARATIVE ON PURPOSE ──────────────────────────────────────────
// A row does not contain code that writes to Firebase. It contains a
// `want` (pure, synchronous, reads a plain board view) and a list of
// `steps` (plain data).
//
// ⭐ ONE RESOLVER (2026-09-15, evening). The step EXECUTOR that used to
// live in NPC.js is gone: the brain resolves every Tome through the
// player's own `tomeResolver.applyTomeEffect`, via npcResolverBridge,
// so a card cannot behave one way for the human and another for the
// brain. `steps` are executed for exactly ONE kind — `feed` (R129), the
// Treasure feed, which is not a Tome effect on either side (the player
// feeds through ATVFeedModal, the brain through NPC.execFeed). For every
// other row the steps are DOCUMENTATION of what the card does, and the
// import-time assertion below still checks them against the card face —
// a row whose steps disagree with the printed text throws, exactly as
// before, because the row is still where the brain's WANTING is written.
//
// ── LANES ───────────────────────────────────────────────────────────
//   "activated"  — the brain flips or re-activates it and an effect
//                  resolves headlessly. The bulk of the table.
//   "standing"   — the value is being face-up. Flip once, NEVER charge
//                  again. (Taunt walls, bonded Treasures, Akula.)
//   "reactive"   — the card IS a counter; ChainLaw.trueCounterOptions
//                  already reaches it generically by ilk and level from
//                  the hand or a face-down rift. Proactively flipping
//                  it throws the counter away, so want() is null on the
//                  proactive lane BY DESIGN, not by omission.
//   "human-only" — cannot be resolved headlessly, and the row says why.
//                  The brain declines to place it rather than squat a
//                  rift with it. A declared gap beats a fake resolution.
//
// ── HONESTY NOTES ───────────────────────────────────────────────────
// Where a row's resolution is an approximation of the printed text, the
// row says so in `note` and the balancer prints it. Nothing in here is
// marked handled on the strength of a coin flip standing in for real
// text. The nine Shadow Gambling Tomes used to be exactly that — ONE
// `Math.random() > 0.5` for all nine, ignoring dice counts, the streak,
// Casino Royale's banked fortune and Underdog's pending bet. They now
// roll their own printed odds.
// ════════════════════════════════════════════════════════════════════

import { TOMES } from "./cards";
import { MAX_ANIMA, MAX_KHAO_ABS, RESURRECT_LIMIT, canPayVoluntary, ATV_FEED } from "./rules";
import { baseId } from "./necroSeal";

// ─────────────────────────────────────────────────────────────
// LOCAL CATALOGUE + SHAPE HELPERS
// The book must be readable without NPC.js, so it carries its own
// tiny lookups rather than importing the brain's.
// ─────────────────────────────────────────────────────────────
import { ESCENDANTS, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";

const BOOK_CARDS = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES,
                    ...PROPHECIES, ...CURIOS, ...ESCLINGS];
const BY_ID = new Map(BOOK_CARDS.map(c => [c.id, c]));
export const bookCard = (id) => BY_ID.get(baseId(id)) || null;

// Firebase turns [null, x, null] into {1:x}. Same normalizer as NPC.riftArray.
export const rift3 = (v) => {
  const out = [null, null, null];
  if (Array.isArray(v)) { for (let i = 0; i < 3; i++) out[i] = v[i] ?? null; }
  else if (v && typeof v === "object") {
    Object.keys(v).forEach(k => { const n = parseInt(k, 10); if (n >= 0 && n < 3) out[n] = v[k]; });
  }
  return out;
};

const TAUNT_IDS = new Set([
  "tome_khao_duhmdumn_beb", "tome_khao_duhmdumn_champion", "tome_khao_duhmdumn_lord",
]);

// Mirror of Shatterfield.getRequiredTarget / NPC.getRequiredTargetNpc,
// kept here so `want` can ask "is my ruin even legal right now" without
// reaching back into the brain. An escling-level taunt outranks a Tome.
export function requiredTargetOf(field) {
  if (!field) return null;
  const esc = field.esclings || [];
  const ei = esc.findIndex(s => s?.escendant && s?.mustBeRuinedFirst);
  if (ei >= 0) return { kind: "escling", slotIdx: ei, id: esc[ei].escendant };
  const tomes = rift3(field.tomes);
  const ti = tomes.findIndex(t => t?.id && !t.faceDown && TAUNT_IDS.has(t.id));
  if (ti >= 0) return { kind: "tome", slotIdx: ti, id: tomes[ti].id };
  return null;
}

// ── FILTER SPEC ─────────────────────────────────────────────────────
// A serialisable card predicate. `anyOf` lets one search offer two
// unrelated shapes (the Ritual Tomes offer "a Tome up to L2 OR a
// Curio", which is not expressible as a single conjunction).
export function matchesFilter(card, f) {
  if (!card || !f) return !!card;
  if (f.anyOf) return f.anyOf.some(sub => matchesFilter(card, sub));
  if (f.types && !f.types.includes(card.type)) return false;
  if (f.ilk && card.ilk !== f.ilk) return false;
  if (f.notIlk && card.ilk === f.notIlk) return false;
  if (f.level !== undefined && (card.level || 1) !== f.level) return false;
  if (f.maxLevel !== undefined && (card.level || 1) > f.maxLevel) return false;
  if (f.notId && card.id === f.notId) return false;
  return true;
}

// Count how many cards in a pile satisfy a filter. Grimoire/Luxmoire
// are {id: qty} maps; the Necropolis is a flat array (and its ids may
// carry a Graveshift seal, hence baseId through bookCard).
export function poolCount(pile, filter, shape) {
  if (!pile) return 0;
  if (shape === "array") {
    return (pile || []).reduce((n, id) => n + (matchesFilter(bookCard(id), filter) ? 1 : 0), 0);
  }
  return Object.entries(pile || {}).reduce(
    (n, [id, qty]) => n + (matchesFilter(bookCard(id), filter) ? (qty || 0) : 0), 0);
}

// ─────────────────────────────────────────────────────────────
// THE BOARD VIEW — what `want` is allowed to see
// Built by NPC.buildTomeView from ONE fresh match read. Pure data;
// no promises, no writers. Anything a row needs must be in here, so
// that a row can never accidentally become a second dispatcher.
// ─────────────────────────────────────────────────────────────
export function buildView({ turn, me, opp, slotIdx = -1, tome = null, seatCount = 2 }) {
  const myField = me?.field || {};
  const oppField = opp?.field || {};
  const myTomes = rift3(myField.tomes);
  const oppTomes = rift3(oppField.tomes);
  const beingsOf = (f) => (f.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
  const soulOf = (f) => (f.esclings || []).flatMap((s, i) =>
    (s?.soul || []).map(sid => ({ sid, slotIdx: i, type: bookCard(sid)?.type || null })));
  return {
    turn: turn || 0,
    // ── R103 · HOW WIDE THE TABLE IS (2026-08-26) ──────────────
    // A duration written in FULL turns has to become half-turns to be
    // stamped on the board, and "one full turn" is every seat acting
    // once. The view carries the number so a `durationKeys` step never
    // has to reach for a match object it does not hold. 1v1 → 2, and
    // every existing caller that omits it gets exactly that.
    seatCount: Math.max(2, Number(seatCount) || 2),
    me, opp,
    myField, oppField, myTomes, oppTomes,
    myBeings: beingsOf(myField),
    oppBeings: beingsOf(oppField),
    myStanding: beingsOf(myField).filter(({ s }) => !s.fallen),
    oppStanding: beingsOf(oppField).filter(({ s }) => !s.fallen),
    oppSoul: soulOf(oppField),
    oppCurios: (oppField.curios || []).map((c, i) => ({ id: c, i })).filter(({ id }) => id),
    oppProphecy: oppField.prophecy || null,
    myMrf: requiredTargetOf(myField),
    oppMrf: requiredTargetOf(oppField),
    grim: me?.grimoire || {},
    lux: me?.luxmoire || {},
    nec: me?.necropolis || [],
    anima: me?.anima ?? 30,
    khao: me?.khao ?? 0,
    maxKhao: me?.maxKhao ?? 3,
    oppAnima: opp?.anima ?? 30,
    resurrected: me?.resurrectedCards || [],
    slotIdx, tome,
    onField: slotIdx >= 0 && !!tome,
  };
}

// ── SHARED `want` PREDICATES ────────────────────────────────────────
const G = (V, f) => poolCount(V.grim, f, "map");
const L = (V, f) => poolCount(V.lux, f, "map");
const N = (V, f) => poolCount(V.nec, f, "array");

const oppHasTomeOrCurio = (V) => V.oppTomes.some(t => t?.id) || V.oppCurios.length > 0;
const oppHasFaceUpTome  = (V) => V.oppTomes.some(t => t?.id && !t.faceDown);
const oppSoulOf = (V, types) => V.oppSoul.filter(x => types.includes(x.type));
// A being-ruin (and a soul-strip) is ILLEGAL while a must-ruin-first
// target stands, so a Tome that does one is worth nothing right now.
const beingRuinLegal = (V) => V.oppBeings.length > 0 && !V.oppMrf;
// A Tome/Curio ruin is legal even under MRF — it just gets FORCED onto
// the MRF tome. Same rule the player side follows.
const emptyEsclingSlot = (V) =>
  (V.myField.esclings || []).some(s => s?.escling && !s?.escendant);
// Can this Necropolis still legally give a being back? R44 counts per
// COPY, and a candidate already at its rung's limit is not a candidate.
function resurrectableCount(V, maxLevel) {
  const used = new Map();
  (V.resurrected || []).forEach(id => used.set(id, (used.get(id) || 0) + 1));
  const seen = new Set();
  let n = 0;
  (V.nec || []).forEach(rawId => {
    const c = bookCard(rawId);
    if (c?.type !== "Escendant") return;
    if ((c.level || 1) > maxLevel) return;
    const b = baseId(rawId);
    if (seen.has(b)) return;
    const cap = RESURRECT_LIMIT[`L${c.level}`] ?? 1;
    if ((used.get(rawId) || used.get(b) || 0) >= cap) return;
    seen.add(b); n++;
  });
  return n;
}
// A being of mine that has nothing bonded to it yet and is standing.
const bondableBeing = (V) => {
  const taken = new Set(V.myTomes.filter(t => t && t.wieldedBy !== undefined && t.wieldedBy !== null)
    .map(t => t.wieldedBy));
  return V.myStanding.find(({ i }) => !taken.has(i)) || V.myStanding[0] || null;
};
// Evidence the adversary actually plays the ilk this ward answers.
// Nothing can see a face-down rift, so "they have a rift occupied, or
// their Necropolis proves they run the ilk" is the honest test.
const oppRunsIlk = (V, ilk) =>
  V.oppTomes.some(t => t?.id && (t.faceDown || bookCard(t.id)?.ilk === ilk)) ||
  (V.opp?.necropolis || []).some(id => bookCard(id)?.ilk === ilk);

// ─────────────────────────────────────────────────────────────
// THE TABLE
// ─────────────────────────────────────────────────────────────
// mode  — which difficulty tier will re-activate it (mirrors the old
//         GOOD / AGGRESSIVE / SSG split, now derived from one place):
//         "good" Beb+, "aggro" Champion+, "ssg" Lord, "utility"
//         Champion+ (searches and draws — a Beb does not tutor).
// standing — TRUE means the fire step is a no-op and the value is the
//         card being face-up. Flip once; never re-charge. THIS IS THE
//         FLAG THAT KILLED THE PAY-FOR-NOTHING HOLE.
// ─────────────────────────────────────────────────────────────

const R = {};

// ── `standing` IS DERIVED FROM THE LANE. IT IS NOT A SECOND OPINION ──
// ⚠ THIS IS A RULING ABOUT THE TABLE ITSELF, not a repair to one row,
// so it is written where the table is built.
//
// `standing: true` used to be a free-standing key a row could set
// beside its lane, and two rows set BOTH `lane: "activated"` and
// `standing: true`. Those two words say opposite things — "the brain
// re-activates me every Turn" and "flipping me bought everything I
// do" — and NPC.js's activateOneTome tests the FLAG first:
//
//     if (tomeIsStanding(card.id)) continue;
//
// So tome_necklace_death and tome_necklace_courage, both of which
// carry a `feed` step and a want() written explicitly for repeated
// per-Turn feeding, were fed EXACTLY ONCE — at the flip, by
// flipOneTome, the executor's only other caller — and never again.
// Congealed Death could never reach 8 Anima and so could never ruin a
// being; the Necklace of Courage could never reach 6 Khao and so could
// never grant its +6. Two cards permanently useless, and the table
// declared it in writing on the row itself.
//
// A check that catches that is a check somebody has to remember to
// keep. So the disagreement is made UNSAYABLE instead: there is now
// ONE declaration — the lane — and `standing` is computed from it. A
// spec that still passes `standing` explicitly does not get quietly
// ignored either; the gate at the bottom of this file throws on it by
// name, the same way a missing row throws.
// ── ⭐ R129 · ONE TABLE FOR A FED TREASURE'S THREE NUMBERS ───────
// The currency a Treasure eats, how much it takes per Turn and the
// threshold it pays out at were hand-typed HERE, in Shatterfield's feed
// modal, in its till and in NPC.execFeed — four copies of "anima, 4, 8"
// and "khao, 3, 6". `rules.ATV_FEED` is the one table now (R129), and
// this builder is how a row reads it, so the row cannot disagree with the
// card. A future fed Treasure is added to ATV_FEED and gets a row that
// carries no numbers at all.
//
// ⚠ THE STEP STILL CARRIES PLAIN NUMBERS, deliberately: `steps` is DATA
// that NPC.js's executor reads and that the harness serialises, so it may
// not hold a function or a reference. What changes is that the numbers
// are DERIVED at build time rather than typed — one source, four readers.
const feedStep = (id, onFull) => {
  const spec = ATV_FEED[id];
  if (!spec) throw new Error(`npcTomeBook: ${id} has a feed step but no rules.ATV_FEED row`);
  return { k: "feed", currency: spec.currency, per: spec.perTurn, cap: spec.threshold, onFull };
};
const feedCap = (id) => ATV_FEED[id].threshold;
const feedPer = (id) => ATV_FEED[id].perTurn;
// ── ⭐ WHAT A FEED COSTS *ON THIS LANE* ──────────────────────
// A fed Treasure is reached by TWO lanes and they charge differently, and
// want() is asked by both and cannot tell them apart from the outside:
//
//   · flipOneTome      — the card is FACE-DOWN and is being turned over.
//                        That is PLAYING it, so the printed `khaoCost` is
//                        owed, and the food is owed on top.
//   · activateOneTome  — the card is already face-up. R105's fare is
//                        exempt on a feed row (derived from the `feed`
//                        step, 2026-09-15), so ONLY the food is owed.
//
// ⚠ THIS COST A MEASURED REGRESSION AND THE BALANCER CAUGHT IT. The
// handoff said want() could drop to the food alone now the fare was gone,
// I did that, and `tome_necklace_courage` came back as 1 no-op / 3 Khao
// at seed 3: the brain flipped the card, paid the 3-Khao PLACEMENT fare,
// and then execFeed could not afford the 3-Khao food. The exemption is
// real but it is only on the RE-ACTIVATION lane; the flip fare never went
// anywhere. tools/tomebook.selftest.mjs cannot see this — it drives every
// row once, against a fresh board, and never flips a Treasure with
// exactly its own fare in the bank.
//
// So the number is derived per lane instead of picked. `V.tome.faceDown`
// IS the lane: flipOneTome only ever looks at face-down Tomes.
const feedKhaoNeeded = (id, V) => {
  const food = ATV_FEED[id].currency === "khao" ? ATV_FEED[id].perTurn : 0;
  const fare = V?.tome?.faceDown ? (bookCard(id)?.khaoCost || 0) : 0;
  return food + fare;
};

const _explicitStanding = [];
const row = (id, spec) => {
  if (Object.prototype.hasOwnProperty.call(spec, "standing")) _explicitStanding.push(id);
  const { standing: _ignored, ...rest } = spec;
  R[id] = { id, want: () => 1, steps: [], ...rest, standing: rest.lane === "standing" };
};

// ═══ CONSTRUCTION ═══════════════════════════════════════════════════
// ── THE TWO THAT PRINT A MOMENT ──────────────────────────
// Khao Garden and Khao Arcadia are the only Construction Tomes whose text
// names WHEN: *"Add +N to your Max Khao AT THE END OF THE TURN."* Khao
// Paradise, beside them, says no such thing and rises immediately — the
// difference is printed, so it is real.
//
// ⭐ AND "THE END OF THE TURN" IS THE HALF-TURN. RULED 2026-09-14, and
// the ruling is about the DIFFERENCE between a named moment and a bare
// number. caP'n:
//
//   *"3 turns or any type of broad reference always refers to a full
//   turn; I will specify with words like 'on your turn' or 'your
//   opponent's turn' something like that, but 'for 3 turns' is way too
//   broad and that means 6 half turns, once the 6 half turns are over,
//   you can proceed."*
//
// So a BARE number of turns is full Turns and the window is EXCLUSIVE
// (read `rules.durationStillRunning`, never a comparison of your own —
// no row in this book hardcodes a half-turn count, and none may). But a
// NAMED moment is the moment it names: "at the end of the turn" is the
// end of the owner's own half-turn, which is where the board already
// delivers it. My earlier note filed this as uncertain; it is settled,
// and only the brain's TIMING is still an approximation.
// The `maxKhao` step applies at once (NPC.js has no deferred step kind), so
// the brain banks the ceiling one half-turn early and can spend into it on
// the Turn it paid. Declared here rather than faked, per this file's
// honesty rule, and handed to whoever owns NPC.js.
row("tome_khao_garden", {
  lane: "activated", mode: "good", steps: [{ k: "maxKhao", n: 1 }],
  want: V => V.maxKhao < MAX_KHAO_ABS ? 40 : null,
  note: "RULED 2026-09-14: \"at the end of the turn\" is a NAMED moment and means the owner's own half-turn (a BARE \"N turns\" would mean full Turns, exclusive). The board delivers it there; the brain's maxKhao step raises it immediately, so the brain can spend into the new ceiling on the Turn it paid. Needs a deferred step kind in NPC.js \u2014 an approximation of the moment, not of the amount, the fare or the cadence.",
});
row("tome_khao_arcadia", {
  lane: "activated", mode: "good", steps: [{ k: "maxKhao", n: 2 }],
  want: V => V.maxKhao < MAX_KHAO_ABS ? 45 : null,
  note: "same NAMED end-of-turn moment as Khao Garden (ruled 2026-09-14), same approximation. The OP2 cadence and the fare ARE enforced on this lane (cooldownHalfTurns + activationFare); it is only the moment that is early.",
});
row("tome_khao_paradise", {
  lane: "activated", mode: "good",
  steps: [{ k: "maxKhao", n: 2 }, { k: "selfIntangibleTome" }],
  want: V => V.maxKhao < MAX_KHAO_ABS ? 45 : null,
});
row("tome_bhodi_tree", {
  lane: "activated", mode: "good", steps: [{ k: "animaSelf", n: 1 }],
  want: V => V.anima < MAX_ANIMA ? 30 : null,
});
row("tome_anima_house", {
  lane: "activated", mode: "good", steps: [{ k: "animaSelf", n: 2 }],
  want: V => V.anima < MAX_ANIMA ? 35 : null,
});
row("tome_stars_and_skies", {
  lane: "activated", mode: "good",
  steps: [{ k: "animaSelf", n: 5 }, { k: "selfIntangibleTome" }],
  want: V => V.anima < MAX_ANIMA ? 50 : null,
});
// The three taunt walls. Their whole text is "your adversary must ruin
// this before attacking another" — that is a STANDING fact about a
// face-up card, so the flip buys it and re-activation buys nothing.
// With no adversary being on the board there is nothing to taunt, and
// the fare buys nothing THIS turn — the card waits face-down for free.
["tome_khao_duhmdumn_beb", "tome_khao_duhmdumn_champion", "tome_khao_duhmdumn_lord"]
  .forEach(id => row(id, {
    lane: "standing", mode: "good",
    steps: [{ k: "duhmdumn" }],
    want: V => V.oppBeings.length > 0 ? 55 : null,
  }));

// ═══ TREASURE ═══════════════════════════════════════════════════════
// The two ATV necklaces used to be the file's entire "cannot use" list,
// blacklisted because feeding was a player-side modal and NPC.js had no
// `fed` anywhere in it. Feeding is not a modal, it is an arithmetic
// step with a threshold, and the step kind below is it. The blacklist
// is gone.
//
// ══ ⭐ A TREASURE'S PRINTED Lyf IS ITS DURABILITY (caP'n, 2026-09-14) ══
// Asked how the Necklace of Joyous Courage lives, caP'n described the
// whole card and then generalised it, verbatim:
//
//   *"That Tome has 3 Lyf, so it remains on the board (most if not all
//   treasures behave this way) until ruined by Lyt attack or other
//   means."*
//
// So a Treasure's `lyf` is HOW LONG IT SURVIVES — not a price, not a
// timer. Same reading R105 already carries (*"Lyf printed on a Treasure
// is its durability and is never payable"*), stated HERE because this is
// the section a future reader opens when a Treasure misbehaves, and
// because it settles two things at once:
//
//   1. A TREASURE IS NOT VOIDED BY PAYING OUT. It pays its threshold and
//      STAYS, on its printed Lyf, until something ruins it. Both Necklace
//      rows carried a `{ k: "ruinSelf" }` in their `onFull` — a
//      lifecycle the card does not print. Gone.
//   2. ATV — "Active Till Void" — IS VOIDED THE ORDINARY WAY. caP'n,
//      asked what can void an ATV card: *"yes your adversary can choose
//      to just not fulfil the requirements or you. also its still a tome
//      and many cards can outright ruin cards or tomes in general."*
//      There was never a missing mechanic to find. An ATV Tome is a
//      Tome: ruin it and it is void.
//
// ⚠ AND THE STORED CURRENCY DIES WITH IT. caP'n: *"you just give it
// Khao and it stores it for you, IF THE CARD IS RUINED THE KHAO IS
// GONE."* That risk is what the card sells; a refund would delete it.
// Nothing here and nothing in NPC.execFeed refunds `fed`, and nothing
// ever should.
//
// ══ ⭐ AND IT RECHARGES (caP'n, 2026-09-15) ═══════════════════
// Asked whether a Necklace can be fed again once it has paid out:
//
//   *"yes, it can be fed after use, it needs to be ruined for it be
//   taken off the shatterfield."*
//
// So the WHOLE LIFECYCLE, stated once, here, because it took three
// rulings to assemble and the next reader should not have to:
//   1. PLAY it — the printed `khaoCost` is the fare, paid once.
//   2. FEED it — up to `perTurn` of its printed currency, per Turn, by
//      the OWNER only. No second fare, ever: the food IS the cost.
//   3. At `threshold`, it PAYS OUT, and the store CLEARS.
//   4. Go to 2. Indefinitely.
//   5. It leaves the Shatterfield only when something RUINS it — and
//      whatever is stored at that moment is gone.
//
// ⚠ SO `perTurn` IS THE ONLY BRAKE ON THE CARD, and it went from
// cosmetic to load-bearing the moment step 4 became real. Before the
// recharge ruling a wrong allowance cost you one cycle; now it is the
// rate of a permanent engine. It is read from rules.ATV_FEED and typed
// nowhere in this file — see `feedStep`.
//
// ⚠ THE CLEAR IS NOT A REFUND. Step 3 empties the store because the
// store was SPENT; step 5 destroys it because the card died. A path that
// conflates them would hand the owner their Khao back on a ruin, which
// is the one thing the risk clause above forbids.
row("tome_necklace_death", {
  lane: "activated", mode: "aggro",
  // ⭐ NO `ruinSelf`. See the durability banner above: the Treasure pays
  // its threshold and STAYS, on its 2 printed Lyf, until something ruins
  // it. The row used to void the card on payout — a lifecycle the printed
  // text does not have. "After feeding it 8 Anima, ruin 1 being and its
  // Soul" says nothing about the Necklace.
  steps: [feedStep("tome_necklace_death",
            [{ k: "ruin", kinds: ["being"], n: 1, andSoul: true }])],
  // Feeding 8 Anima to kill a being is only a trade worth making if
  // there is a being to kill and the till can stand the payment (R96:
  // a voluntary payment may never take you to 0). And a Treasure may
  // be fed ONCE PER TURN — asking again the same turn is a fare for a
  // step that will refuse itself, which is the whole bug this table
  // exists to kill.
  //
  // ⭐ THE FOOD IS THE COST — THERE IS NO SECOND FARE, AND THE LANE NOW
  // AGREES (caP'n 2026-09-14; the executor landed 2026-09-15). I read
  // R105's naming of ATV as "a feed is an activation, so it re-pays the
  // printed Khao", wrote that here, and asked. He answered by describing
  // the card's whole life and there is no per-feed fare anywhere in it:
  // the printed `khaoCost` is the fare to PLAY the Treasure and the
  // currency you feed it is the card's own mechanic on top. Shatterfield
  // had it right all along.
  //
  // activateOneTome now DERIVES the exemption from the mechanic rather
  // than from a marker — a row carrying a `feed` step gets
  // `{ khao: 0, anima: 0 }` and everything else still pays
  // `activationFare`. That is a better shape than the `fareFreeStep`
  // marker I proposed: a marker is a second place to remember something,
  // and a future fed Treasure that forgot it would be billed again with
  // nothing failing. Measured on landing: this card 2 Khao → 0, Joyous
  // Courage 3 → 0, Khao Garden still pays its full 2. R105 intact, not a
  // blanket amnesty.
  //
  // ⚠ THE `>= cap` GUARD IS A SAFETY NET, NOT A LIFECYCLE CLAIM. caP'n
  // ruled on 2026-09-15 that a fed Treasure RECHARGES — *"yes, it can be
  // fed after use, it needs to be ruined for it be taken off the
  // shatterfield."* So a correct executor CLEARS the store on payout and
  // `fed` never sits at the threshold; this line would then never fire.
  // Shatterfield has landed that (it writes `fed: 0` at both payouts);
  // NPC.execFeed has NOT, so on the brain's side the card still pays out
  // once and then sits full. The guard is what keeps that from becoming a
  // wanted-then-did-nothing fire, and it is safe under BOTH executors —
  // which is exactly why it stays until the brain's half lands.
  want: V => {
    if (V.tome && V.tome.fedThisTurn === V.turn) return null;
    if ((V.tome?.fed || 0) >= feedCap("tome_necklace_death")) return null;
    // R96 on the feed itself, plus headroom for the whole run: reaching
    // the threshold costs the full 8 Anima across two or more Turns, and
    // a brain that starts a run it cannot finish has paid for nothing.
    return (V.oppBeings.length > 0
            && canPayVoluntary(V.anima, feedPer("tome_necklace_death"))
            && V.anima > feedCap("tome_necklace_death")) ? 45 : null;
  },
});
// ⭐ THE CARD caP'n DESCRIBED CLAUSE FOR CLAUSE (2026-09-14). Asked how
// feeding works, he gave the whole life of this one:
//
//   *"you(the player) can only feed it, no one else, then once you have
//   fed the necklace 6 Khao (so you just give it Khao and it stores it
//   for you, if the card is ruined the Khao is gone) then once its full
//   of 6 of the owner's Khao, the owner gets 6 Anima. That Tome has 3
//   Lyf, so it remains on the board … until ruined."*
//
// Against the printed face — L2 Treasure, khaoCost 3, lyf 3, ATV, *"Feed
// this Treasure up to 3 Khao per turn; after feeding it 6 Khao, gain 6
// Anima"* — the ruling IS the card, clause for clause:
//   · the food is KHAO (this row had that right), up to 3 per Turn;
//   · the OWNER feeds it and nobody else — execFeed only ever writes the
//     brain's own rift, so that holds structurally rather than by a check;
//   · the Khao is STORED, and LOST if the Treasure is ruined;
//   · at 6 stored the owner gains 6 Anima;
//   · and then it STAYS, on its 3 Lyf. The `ruinSelf` is gone.
//   · and the store CLEARS and fills again — ruled 2026-09-15, see the
//     lifecycle in the durability banner above. Six Anima for six Khao,
//     on repeat, for as long as the Treasure survives.
row("tome_necklace_courage", {
  lane: "activated", mode: "good",
  steps: [feedStep("tome_necklace_courage", [{ k: "animaSelf", n: 6 }])],
  // ⭐ 3 ON ONE LANE, 6 ON THE OTHER — SO IT IS DERIVED, NOT PICKED.
  // This held a flat 6 because the brain used to pay the 3-Khao fare AND
  // the 3-Khao feed on every firing. activateOneTome stopped charging the
  // feed lane on 2026-09-15, so on RE-ACTIVATION the food is the only
  // cost and 3 is honest. But flipOneTome still charges the printed
  // `khaoCost` to PLAY the Treasure — rightly — so on the FLIP it is
  // still 6. A flat 3 made the brain flip the card, pay the fare, and
  // then fail to feed it: 1 no-op, 3 Khao, measured at seed 3. See
  // `feedKhaoNeeded` above for the derivation and for why tomebook
  // cannot see this and the balancer can.
  //
  // ⭐ AND IT RECHARGES, WHICH MAKES THIS REPEATABLE INCOME. caP'n,
  // 2026-09-15: *"yes, it can be fed after use, it needs to be ruined for
  // it be taken off the shatterfield."* So the store CLEARS at payout and
  // fills again, indefinitely; the card leaves the field only when
  // something ruins it. This is 6 Anima for 6 Khao, on a two-Turn cycle,
  // for as long as the Treasure survives — not a one-off.
  //
  // ⚠ SO THE PER-TURN ALLOWANCE IS NOW THE ONLY BRAKE ON THE CARD, and
  // `perTurn` went from cosmetic to load-bearing overnight. It is read
  // from rules.ATV_FEED (R129) and typed nowhere, which is the whole
  // point of that table: the brake and the printed text cannot drift.
  //
  // ⚠ THE BRAIN CANNOT YET COLLECT THE SECOND CYCLE. NPC.execFeed does
  // not clear the store on payout, so on the brain's side this pays once
  // and then sits full while the player's recharges. Handed off; the
  // `>= cap` guard in want() is what keeps the difference from becoming a
  // fire that moves nothing.
  want: V => {
    if (V.tome && V.tome.fedThisTurn === V.turn) return null;
    if ((V.tome?.fed || 0) >= feedCap("tome_necklace_courage")) return null;
    return (V.anima < MAX_ANIMA
            && V.khao >= feedKhaoNeeded("tome_necklace_courage", V)) ? 35 : null;
  },
  note: "caP'n described this card in full on 2026-09-14 and the printed face is exact: KHAO, up to 3 a Turn, owner only, stored on the card, LOST if it is ruined, 6 Anima at 6 stored, and the Treasure STAYS afterwards on its printed Lyf. RULED 2026-09-15: it RECHARGES \u2014 the store clears at payout and can be filled again indefinitely, so this is repeatable income and the per-Turn allowance is the only brake. Currency, allowance and threshold all come from rules.ATV_FEED (R129). The food is the card's mechanic, not a second fare \u2014 the brain's feed lane stopped charging one on 2026-09-15. Still open on the brain's side: NPC.execFeed does not clear the store on payout, so it collects one cycle where the player collects many.",
});
row("tome_necklace_akula", {
  lane: "standing", mode: "good", steps: [],
  // "+1 Anima whenever a being is ruined" is read by a listener, not
  // fired here. NPC.js now carries that listener (fireAkulaOnRuin) on
  // its own ruin paths, so the card is real on both sides.
  want: V => (V.anima < MAX_ANIMA && (V.oppBeings.length > 0 || V.myBeings.length > 0)) ? 30 : null,
});
row("tome_lightning_blade", {
  lane: "standing", mode: "aggro",
  steps: [{ k: "bond", grant: "lightning" }],
  // Bond is the whole play; the dim fires from doAttacks when the
  // wielder swings. No wielder, or nothing to dim, and it is theatre.
  want: V => (bondableBeing(V) && V.oppBeings.length > 0) ? 40 : null,
});
row("tome_sword_of_shadows", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "bond", grant: "sword" }, { k: "extraAttack" }],
  want: V => {
    const w = bondableBeing(V);
    if (!w) return null;
    if (V.oppBeings.length === 0 && V.oppAnima <= 0) return null;
    return 48;
  },
});
row("tome_weeping_dagger", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "ruin", kinds: ["being"], n: 1, andSoul: true }],
  want: V => beingRuinLegal(V) ? 70 : null,
});
row("tome_gauntlets_azka", {
  lane: "standing", mode: "good",
  steps: [{ k: "bond", grant: "lyt" }],
  want: V => bondableBeing(V) ? 40 : null,
});
row("tome_boots_wing_walking", {
  lane: "activated", mode: "good",
  steps: [{ k: "selfIntangibleBeing" }],
  // Intangibility is worth Khao only while somebody can swing at you.
  want: V => (V.myStanding.some(({ s }) => !s.intangible) && V.oppStanding.length > 0) ? 30 : null,
});
row("tome_chief_calidum", {
  lane: "activated", mode: "good",
  steps: [{ k: "lendLyf", n: 3, turns: 3 }],
  note: "printed text lends to ANY 1 card with Lyf, either side of the board (the player's picker offers both). The brain only ever lends to its own strongest standing being — narrower than the card, and deliberately so: a Lyf gift to an adversary card has no read here worth writing.",
  want: V => {
    if (V.tome?.cuirassLentTo) return null;         // already lending
    if ((V.tome?.currentLyf ?? 3) < 3) return null; // nothing left to lend
    return V.myStanding.length > 0 ? 28 : null;
  },
});

// ═══ NIHIL ══════════════════════════════════════════════════════════
// Nine cards, and the honest split is 1 / 1 / 3 / 4.
//
// TWO of them share almost identical text — "nullify the effect, or
// activation, of 1 of your adversary's cards" — and they DO NOT get the
// same lane, for a reason that is entirely about ChainLaw's floors and
// is worth spelling out because it looks like an inconsistency:
//
//   ChainLaw.trueCounterOptions picks counters generically, by ilk and
//   level, never by id. From the HAND it accepts level 3 only ("hand
//   instants are L3 by law"); from a FACE-DOWN rift it accepts level 2
//   and up ("ambushes are L2+").
//
//   • Cleansing Burst is Nihil L2, so a face-down copy is already a
//     live counter and flipping it proactively THROWS THAT AWAY.
//     Reactive lane; want() is null on the proactive lane by design.
//   • Rays of Hope is Nihil L1 — below BOTH floors. It is not reachable
//     on the counter lane at all, from either place. Filing it as
//     "reactive" would have been a comfortable lie that left one more
//     card permanently inert. Its proactive body is real and is what
//     the player side writes: nihilShieldActive, a standing shield that
//     eats the next opposing card effect this turn.
row("tome_cleansing_burst", {
  lane: "reactive", mode: "utility", steps: [],
  want: () => null,
  note: "counter-only, and it IS reachable there: Nihil L2 clears ChainLaw's face-down floor, so a proactive flip would spend a live ambush for a weaker standing shield.",
});
row("tome_rays_of_hope", {
  lane: "activated", mode: "good",
  steps: [{ k: "flags", set: { nihilShieldActive: true, nihilRuinOmnia: false },
            turnKeys: { nihilShieldTurn: 0 } }],
  // The shield only pays if the adversary still has something to fire:
  // a standing being with a soul on it, a Tome in a rift, or a Curio.
  want: V => {
    if (V.myField.nihilShieldActive && V.myField.nihilShieldTurn === V.turn) return null;
    const theyCanAct = V.oppTomes.some(t => t?.id) || V.oppCurios.length > 0 ||
                       V.oppSoul.length > 0;
    return theyCanAct ? 28 : null;
  },
  note: "Nihil L1 sits BELOW ChainLaw's counter floors (L3 from hand, L2+ face-down), so it can never be used as a counter by either side's brain. The proactive nihilShield is its only reachable body — that is the card as the engine can actually run it, not the card as printed.",
});
// THREE are one-shot wards. C13 canon: they stamp a ward that holds
// across turns until an attack consumes it, so playing one proactively
// is a real board state and not a wasted interrupt.
row("tome_radiant_aura", {
  lane: "activated", mode: "good", steps: [{ k: "ward", scope: "cards" }],
  want: V => (V.oppStanding.length > 0 &&
    (V.myStanding.some(({ s }) => !s.nullifyNextAttack) ||
     V.myTomes.some(t => t?.id && !t.nullifyFirstRuin))) ? 34 : null,
  alsoReactive: true,
});
row("tome_lyt_footed", {
  lane: "activated", mode: "good", steps: [{ k: "ward", scope: "beings" }],
  want: V => (V.oppStanding.length > 0 && V.myStanding.some(({ s }) => !s.nullifyNextAttack)) ? 32 : null,
  alsoReactive: true,
});
row("tome_sword_shield_nihil", {
  lane: "activated", mode: "good", steps: [{ k: "ward", scope: "cards" }],
  note: "the three ward Tomes let a human choose what holds the ward; the brain always wards its most-invested standing being first and only falls back to a Tome when no being can take one. Narrower than the picker, and stated.",
  want: V => (V.oppStanding.length > 0 &&
    (V.myStanding.some(({ s }) => !s.nullifyNextAttack) ||
     V.myTomes.some(t => t?.id && !t.nullifyFirstRuin))) ? 36 : null,
  alsoReactive: true,
});
// FOUR are DURATION effects — a flag with a clock on it. These are the
// ones the brief called "standing interrupts": not instants at all.
//
// ⚠ TWO KINDS OF CLOCK, AND THE DIFFERENCE IS R103.
//   `turnKeys`     — a RAW half-turn offset. Use it ONLY for anchors
//                    that mean "this half-turn" (offset 0): the
//                    nihilShield stamp, "protected this turn", "Wall of
//                    Light is up right now".
//   `durationKeys` — a duration the CARD states in TURNS, converted
//                    through rules.durationExpiryTurn. caP'n,
//                    2026-08-26: *"if it's just broad '2 turns' that
//                    means 2 full turns which consists of 4 × 1/2
//                    turns."* A "2 turn" ward is FOUR half-turns.
// Reading a card's printed "2 turns" as a raw offset of 2 halved every
// duration in the game, on both sides of the board.
row("tome_divine_justice", {
  lane: "activated", mode: "good",
  // R103 — "for 2 turns" is TWO FULL TURNS = 4 half-turns in a duel.
  // This row said `turnKeys: { divineJusticeUntilTurn: 2 }`, a RAW
  // half-turn offset, so the brain's ward lasted half as long as the
  // card says. `durationKeys` is the same shape read the right way.
  steps: [{ k: "flags", durationKeys: { divineJusticeUntilTurn: 2 } }],
  want: V => oppRunsIlk(V, "Omnia") ? 40 : null,
  alsoReactive: true,
});
row("tome_shower_of_lyt", {
  lane: "activated", mode: "good",
  steps: [{ k: "flags", set: { protectedFromTomesThisTurn: true }, turnKeys: { protectedTomeTurn: 0 } }],
  want: V => (V.myBeings.length > 0 && V.oppTomes.some(t => t?.id)) ? 26 : null,
});
row("tome_phoenix_aura", {
  lane: "activated", mode: "good",
  // R103 — see Divine Justice above. Two FULL turns, not two halves.
  steps: [{ k: "flags", durationKeys: { protectedFromTomesUntilTurn: 2 } }],
  want: V => (V.myBeings.length > 0 && V.oppTomes.some(t => t?.id)) ? 38 : null,
  alsoReactive: true,
});
row("tome_wall_of_light", {
  lane: "activated", mode: "good",
  steps: [{ k: "flags", set: { wallOfLightActive: true }, turnKeys: { wallOfLightTurn: 0 } }],
  // "If your Anima would be dimmed this turn, absorb it and ADD it."
  // Worth Khao when the adversary can actually reach the Anima, or when
  // the Anima is low enough that any leak matters.
  want: V => {
    const wallStands = V.myStanding.length > 0;
    const reaches = V.oppStanding.some(({ s }) => !wallStands || s.canDirectAnima || s.animaRedirectAll);
    return (reaches || V.anima <= 15) ? 42 : null;
  },
});

// ═══ OMNIA ══════════════════════════════════════════════════════════
// ── R105 · THE PRINTED ANIMA CORNER IS THE FARE, AND IT IS CHARGED ONCE ──
// ⚠ FOUR ROWS CHARGED IT TWICE, AND THE PLAYER PAID IT ONCE.
// Nothing Left, Radiant Armor, Superque and Lyt Hearted are the only four
// Tomes in cards.js with an `animaCost`, and every one of them ALSO prints
// the same charge as prose: *"Dim your own Anima by 1; ruin 1 Tome or
// Curios."* The corner and the sentence are ONE payment written twice.
//
// Both of the brain's activation lanes already charge it as the fare —
// flipOneTome reads `card.animaCost` and activateOneTome reads
// `activationFare(card).anima`, and both pay it through npcSpendAnima
// BEFORE these steps run. So a `{ k: "selfDim", n: 1 }` step on top made
// the brain pay 2 Anima where the board pays 1: the NPC at 3 Anima refused
// a Nothing Left the human at 3 Anima played happily, and the NPC that did
// play it landed a point lower than the human would.
//
// caP'n, 2026-09-13, on what the corners mean: *"the costs at the bottom
// right and/or bottom left … absolutely must be paid for at least once."*
// ONCE. So the step is gone from all four and the fare stands alone.
//
// `canPayVoluntary(V.anima, 1)` STAYS in every one of their want()s — that
// is R96's decision about the fare, asked before the lane spends it, and it
// is the only thing standing between the brain and a payment that would
// zero it. The step was the double charge; the guard never was.
// Magma Carta keeps its `selfDim: 4` on purpose: its animaCost is 0 and the
// four Anima are pure printed effect, not a corner.
row("tome_nothing_left", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "ruin", kinds: ["tome", "curio"], n: 1 }],
  want: V => (oppHasTomeOrCurio(V) && canPayVoluntary(V.anima, 1)) ? 55 : null,
});
row("tome_regnant", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "ruin", kinds: ["tome", "curio"], n: 1 }],
  want: V => oppHasTomeOrCurio(V) ? 58 : null,
});
row("tome_all_or_nothing", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "flags", set: { allOrNothingActive: true } },
          { k: "ruin", kinds: ["tomeFaceUp"], n: 1, optional: true }],
  // The standing interrupt is half the card, so a bare board is not a
  // refusal — but a board where the adversary holds no rift at all
  // means there is nothing for the interrupt to catch either.
  want: V => (V.oppTomes.some(t => t?.id) && !V.myField.allOrNothingActive) ? 60 : null,
  alsoReactive: true,
});
row("tome_radiant_armor", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "ruin", kinds: ["impera"], n: 1 }],   // R105 — the Anima corner is the fare, charged by the lane
  want: V => (oppSoulOf(V, ["Impera"]).length > 0 && !V.oppMrf && canPayVoluntary(V.anima, 1)) ? 52 : null,
  note: "printed text is 'ruin 1 Impera'. Shatterfield used to lump this in with Omnis' Corruption Jet and offer a U'hlti too; the player branch now splits the two (2026-08-25, caP'n: \"lets fix them\"). Both resolvers read the printed text.",
});
row("tome_omnis_corruption", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "ruin", kinds: ["impera", "uhlti"], n: 1 }],
  want: V => (oppSoulOf(V, ["Impera", "U'hlti"]).length > 0 && !V.oppMrf) ? 56 : null,
});

row("tome_superque", {
  lane: "activated", mode: "aggro",
  // R105 — the Anima corner is the fare, charged by the lane; no selfDim step.
  steps: [{ k: "ruin", kinds: ["impera"], n: 1, optional: true },
          { k: "ruin", kinds: ["uhlti"], n: 1, optional: true }],
  want: V => (oppSoulOf(V, ["Impera", "U'hlti"]).length > 0 && !V.oppMrf && canPayVoluntary(V.anima, 1)) ? 65 : null,
  alsoReactive: true,
});
row("tome_wall_of_life", {
  lane: "activated", mode: "aggro",
  // Ruling 5 (NPC.js): the brain never targets a Prophecy — prophecies
  // are structural. So "Non-Being" here is Tome / Curio / soul card.
  steps: [{ k: "ruin", kinds: ["tome", "curio", "impera", "uhlti", "affinity"], n: 1 }],
  want: V => (oppHasTomeOrCurio(V) ||
    (oppSoulOf(V, ["Impera", "U'hlti", "Affinity"]).length > 0 && !V.oppMrf)) ? 54 : null,
  note: "player side also offers the adversary's Prophecy; the brain never targets one (Ruling 5).",
});
row("tome_lyt_hearted", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "ruin", kinds: ["being", "tome"], n: 1 }],   // R105 — Anima corner is the fare
  want: V => {
    if (!canPayVoluntary(V.anima, 1)) return null;
    return (beingRuinLegal(V) || V.oppTomes.some(t => t?.id)) ? 62 : null;
  },
  alsoReactive: true,
});
row("tome_sword_shield_omnis", {
  lane: "activated", mode: "aggro",
  steps: [{ k: "flags", set: { swordShieldOmnisActive: true } },
          { k: "ruin", kinds: ["tomeFaceUp", "impera", "uhlti"], n: 1, optional: true }],
  want: V => {
    if (V.myField.swordShieldOmnisActive) return null;
    const anything = V.oppTomes.some(t => t?.id) || oppSoulOf(V, ["Impera", "U'hlti"]).length > 0;
    return anything ? 64 : null;
  },
  alsoReactive: true,
});

// ═══ ANCIENT SORCERY ════════════════════════════════════════════════
const tutorRow = (id, from, filter, extra = {}) => row(id, {
  lane: "activated", mode: "utility",
  steps: [{ k: "tutor", from, filter, n: 1 }],
  want: V => ((from === "grimoire" ? G(V, filter) : from === "luxmoire" ? L(V, filter) : N(V, filter)) > 0) ? 44 : null,
  ...extra,
});
tutorRow("tome_not_here", "grimoire", { types: ["Tome"], level: 1 });
tutorRow("tome_not_now",  "grimoire", { types: ["Tome"], maxLevel: 2 });
tutorRow("tome_not_ever", "grimoire", { types: ["Tome"], maxLevel: 3 });
// The blood family: a sacrifice THEN a tutor. Both halves must be
// payable or the card is a pure loss — a sacrifice with nothing to
// tutor back is the definition of paying for nothing.
const bloodRow = (id, sacFilter, getFilter) => row(id, {
  lane: "activated", mode: "utility",
  steps: [{ k: "sacrifice", filter: sacFilter }, { k: "tutor", from: "grimoire", filter: getFilter, n: 1 }],
  want: V => {
    const canGive = (V.me?.hand || []).some(hid => matchesFilter(bookCard(hid), sacFilter)) ||
                    G(V, sacFilter) > 0;
    // The tutor pool must survive the sacrifice: if the ONLY sacrificial
    // body is also the only tutor target, the card nets zero.
    if (!canGive) return null;
    return G(V, getFilter) > 0 ? 46 : null;
  },
});
bloodRow("tome_blood_in_blood_out", { types: ["Escendant"] }, { types: ["Escendant"], maxLevel: 1 });
bloodRow("tome_blood_money", {}, { types: ["Escendant"], maxLevel: 2 });
bloodRow("tome_river_of_blood", { types: ["Escendant"] }, { types: ["Escendant"], maxLevel: 3 });
tutorRow("tome_shadow_blanket", "luxmoire", { types: ["Impera"], level: 2 }, {
  note: "printed text says LUXMOIRE and Impera live in the Luxmoire. Shatterfield used to search the Grimoire, where an Impera can never be, so the card was dead in every shipping build; the player branch now reads the Luxmoire (2026-08-25). Both resolvers read the printed text.",
});
tutorRow("tome_inverted_knife", "grimoire", { types: ["U'hlti"] });
row("tome_light_casting_shadow", {
  lane: "activated", mode: "utility",
  steps: [{ k: "tutor", from: "luxmoire", filter: { types: ["Impera"] }, n: 1 },
          { k: "tutor", from: "grimoire", filter: { types: ["U'hlti"] }, n: 1 }],
  want: V => (L(V, { types: ["Impera"] }) > 0 || G(V, { types: ["U'hlti"] }) > 0) ? 50 : null,
  note: "printed text pulls the Impera from the LUXMOIRE and the U'hlti from the Grimoire. Shatterfield used to pull both from the Grimoire; the player branch now names each pile (2026-08-25). Both resolvers read the printed text.",
});

// ═══ RITUAL ═════════════════════════════════════════════════════════
// THE NINE THAT COST THE MOST. Every one of these is a pure activated
// search of the Necropolis with ZERO standing value, and every one of
// them carries an OPT/OP2 tag — so the old PERSISTENT_TOME_TAGS escape
// hatch declared all nine "worth paying" forever. Mausoleum of Omnis is
// 9 Khao every two turns for nothing at all. want() now asks the only
// question that matters: is there anything in the Necropolis to get?
const ritualTutor = (id, filter) => row(id, {
  lane: "activated", mode: "utility", steps: [{ k: "tutor", from: "necropolis", filter, n: 1 }],
  want: V => N(V, filter) > 0 ? 48 : null,
});
ritualTutor("tome_seal_of_omnis",
  { anyOf: [{ types: ["Tome"], maxLevel: 1, notIlk: "Ritual" }, { types: ["Curio"] }] });
ritualTutor("tome_monoliths_of_omnis",
  { anyOf: [{ types: ["Tome"], maxLevel: 2, notIlk: "Ritual" }, { types: ["Curio"] }] });
ritualTutor("tome_mausoleum_of_omnis",
  { anyOf: [{ types: ["Tome"], maxLevel: 3, notIlk: "Ritual" }, { types: ["Curio"] }] });
ritualTutor("tome_seal_of_nihil",      { types: ["Impera"], maxLevel: 1 });
ritualTutor("tome_monoliths_of_nihil", { types: ["Impera"], maxLevel: 2 });
ritualTutor("tome_mausoleum_of_nihil",
  { anyOf: [{ types: ["Impera"], maxLevel: 3 }, { types: ["U'hlti"] }] });
// The Soulcerers put a being back on the FIELD, which needs a landing
// slot as well as a body. R89's once-per-turn paid rite does NOT apply
// (card-effect returns are exempt — see the banner over resurrectOne);
// R44's per-copy tally DOES, on every method.
const soulcerer = (id, maxLevel, orTwoL1 = false, extra = {}) => row(id, {
  lane: "activated", mode: "utility",
  steps: [{ k: "resurrect", maxLevel, orTwoL1 }],
  want: V => (emptyEsclingSlot(V) && resurrectableCount(V, maxLevel) > 0) ? 68 : null,
  ...extra,
});
soulcerer("tome_soulcerers_seal", 1);
soulcerer("tome_soulcerers_monoliths", 2);
soulcerer("tome_soulcerers_mausoleum", 3, true, {
  note: "the '1 being up to L3 OR 2 level-1 beings' choice is a heuristic, not a search: the brain takes the big body when the Necropolis holds an L2 or L3 it can legally return, and two L1s otherwise. A human weighs board shape; this weighs level.",
});

// ═══ SHADOW GAMBLING ════════════════════════════════════════════════
// Nine cards that used to share ONE `Math.random() > 0.5` and a blind
// draw. They now roll their printed odds, honour Casino Royale's banked
// fortune, resolve Underdog's pending bet, and move the streak that
// proph_ace_of_shades reads. A LOSS is a real outcome with a real cost
// (the streak resets, and the bet is forfeit) — that is the card.
row("tome_temptation_manifested", {
  lane: "activated", mode: "ssg",
  steps: [{ k: "ssg", roll: { kind: "dice", dice: 1, need: 1 },
            onWin: [{ k: "tutor", from: "necropolis", filter: {}, n: 1 }] }],
  want: V => (V.nec || []).length > 0 ? 33 : null,
});
row("tome_nevermore", {
  lane: "activated", mode: "ssg",
  steps: [{ k: "ssg", roll: { kind: "dice", dice: 1, need: 1 },
            onWin: [{ k: "tutor", from: "grimoire", filter: {}, n: 1 }] }],
  want: V => Object.keys(V.grim || {}).length > 0 ? 33 : null,
});
row("tome_snake_eyes", {
  lane: "activated", mode: "ssg",
  steps: [{ k: "ssg", roll: { kind: "dice", dice: 2, need: 2 },
            onWin: [{ k: "tutor", from: "grimoire", filter: {}, n: 2 }] }],
  want: V => Object.keys(V.grim || {}).length > 0 ? 30 : null,
});
row("tome_dragon_deyes", {
  lane: "activated", mode: "ssg", steps: [{ k: "dragonDeyes" }],
  // Per-die accounting: I draw on every MISS, the adversary draws on
  // every HIT. With a 1-in-6 chosen number that is 5/3 cards to me and
  // 1/3 to them — but only if my Grimoire can pay out.
  want: V => Object.keys(V.grim || {}).length > 0 ? 36 : null,
});
row("tome_gambits_lucky_cat", {
  lane: "activated", mode: "ssg",
  steps: [{ k: "ssg", roll: { kind: "coin" },
            onWin: [{ k: "tutor", from: "grimoireOrNecropolis", filter: {}, n: 2 }] }],
  want: V => (Object.keys(V.grim || {}).length + (V.nec || []).length) > 0 ? 40 : null,
});
row("tome_casino_royale", {
  lane: "activated", mode: "ssg",
  steps: [{ k: "ssg", roll: { kind: "coin", noAuto: true },
            onWin: [{ k: "flags", set: { nextSGAutoWin: true } }] }],
  // Banking a guaranteed win is worthless with no other gamble to spend
  // it on, and it cannot bank onto itself ("except Casino Royale").
  want: V => {
    if (V.myField.nextSGAutoWin) return null;
    const others = Object.keys(V.grim || {}).concat(V.me?.hand || [])
      .some(id => id !== "tome_casino_royale" && bookCard(id)?.ilk === "Shadow Gambling");
    const onField = V.myTomes.some(t => t?.id && t.id !== "tome_casino_royale" &&
      bookCard(t.id)?.ilk === "Shadow Gambling");
    return (others || onField) ? 25 : null;
  },
});
row("tome_daft_fantasy", {
  lane: "activated", mode: "ssg", steps: [{ k: "daftFantasy" }],
  want: V => poolCount(V.grim, { maxLevel: 3 }, "map") > 0 ? 30 : null,
  note: "the adversary's guess is modelled as a uniform pick among levels 1-3. The printed card is a mind-game the adversary WANTS to lose; a headless adversary has no motive, so this is an approximation and is labelled one.",
});
row("tome_goodbye_horses", {
  lane: "activated", mode: "ssg", steps: [{ k: "goodbyeHorses" }],
  // "Pick 2 TOMES from your Grimoire" is two CARDS, so this counts COPIES
  // — poolCount sums qty. That is the card, and the player's resolver now
  // agrees (it built its pool from Object.keys and refused a Grimoire of
  // duplicates outright). NPC.js's goodbyeHorses executor still builds its
  // pool from Object.keys, so on a duplicate-only Grimoire this want() says
  // yes and the executor finds "fewer than 2" — 8 Khao for nothing, which
  // is the one thing this file exists to prevent. Declared, and handed off.
  want: V => G(V, { types: ["Tome"] }) >= 2 ? 42 : null,
  note: "the adversary's Ilk guess is modelled as a uniform pick among the eight Tome Ilks (7-in-8 to miss, which is what the printed card says). Same mind-game caveat as Daft Fantasy. \u26a0 AND: want() counts COPIES (the card says 2 Tomes, not 2 kinds) while NPC.js's executor still counts distinct ids \u2014 so a Grimoire holding only duplicates pays the fare and moves nothing until that executor counts copies too.",
});
row("tome_underdogs_howl", {
  lane: "activated", mode: "ssg", steps: [{ k: "setBet" }],
  // A bet with no later gamble to settle it just exiles the card.
  want: V => {
    if (V.myField.pendingBet) return null;
    if (Object.keys(V.grim || {}).length === 0) return null;
    const later = Object.keys(V.grim || {}).concat(V.me?.hand || [])
      .some(id => id !== "tome_underdogs_howl" && bookCard(id)?.ilk === "Shadow Gambling");
    return later ? 24 : null;
  },
});

// ═══ CONTRACT ═══════════════════════════════════════════════════════
row("tome_devils_advocate", {
  lane: "activated", mode: "utility",
  steps: [{ k: "drawSelf", n: 1 }, { k: "drawOpp", n: 1 }],
  // Symmetric. If MY Grimoire is dry this is a pure gift to the
  // adversary — the cheapest pay-for-nothing on the board.
  want: V => Object.keys(V.grim || {}).length > 0 ? 40 : null,
});
row("tome_monkey_suite", {
  lane: "activated", mode: "good",
  steps: [{ k: "animaSelf", n: 5 }, { k: "animaOpp", n: 5 }],
  // Symmetric Anima only helps the seat that is behind. Handing the
  // leader five Anima to draw level is a losing trade.
  want: V => (V.anima < MAX_ANIMA && V.anima <= V.oppAnima) ? 44 : null,
});
row("tome_judge_jury", {
  lane: "activated", mode: "utility", steps: [{ k: "judgeJury" }],
  want: V => {
    const f = { types: ["Tome"], ilk: "Contract" };
    return (G(V, f) + N(V, f)) > 0 ? 38 : null;
  },
});
// ⚠ FLAGGED FOR THE DESIGNER, DELIBERATELY NOT CHANGED (2026-08-25).
// This row is `mode: "good"` — i.e. every tier from Beb up will flip it
// — for a card whose standing effect, AS PRINTED, gives the ADVERSARY
// 1 Anima a Turn. If the printed text is the truth, "good" is exactly
// backwards and the brain is paying to heal the other seat. The row is
// scored against what the ENGINE does today (see the note below) and
// the discrepancy is written down rather than resolved by a reviewer
// picking a side: the ruling belongs to caP'n, and the moment it lands
// this row's `mode` — and possibly its whole existence on the good
// lane — changes with it.
// ⭐ RULED 2026-09-14 · THE ANIMA GOES TO THE ADVERSARY, AND THAT MAKES
// THIS A BRIBE RATHER THAN INCOME.
//
// This row scored the card as a small Anima drip TO ITS OWNER and said
// in writing that it was scoring the engine rather than the card,
// because the engine and the printed text disagreed and the ruling was
// not mine to make. It has landed. caP'n, asked which he meant:
//
//   *"it should be given to your adversary, you are giving them a peace
//   offering of sorts like here, 1 Anima every turn you don't attack me.
//   some might take it, others might not think it worth waiting and void
//   it immediately."*
//
// So the PRINTED CARD was right: *"Every turn your adversary doesn't Lyt
// Attack you, THEY gain 1 Anima. You gain 2 Anima when this card is
// ruined."* The engine paid the wrong seat on the wrong condition — it
// walked the adversary's tomes, tested whether the TOME'S OWNER attacked
// (a flag always clear during the other seat's half-turn) and paid the
// OWNER, unconditionally, every Turn. Handed off; the two halves must
// land together, because a board that pays the adversary while this row
// still scores it as income is a brain buying a card to heal the player.
//
// ── WHAT THE CARD ACTUALLY IS ──────────────────────────────
// A STALLING PLAY. You pay 3 Khao to put a standing offer on the table:
// hold your swing and take an Anima a Turn. It buys TIME, and time is
// only worth buying when you are already winning — so:
//
//   · `mode` changes: it was the good tier, it is now utility. The good
//     flag is what makes a Beb flip a card, and a Beb handing the
//     adversary Anima every Turn is the pay-for-nothing bug pointed at
//     the other seat. Champion and Lord read utility; a Beb does not.
//
//     ⚠ THIS SENTENCE ONCE NAMED THE TWO MODES IN QUOTES, DIRECTLY AFTER
//     THE WORD "fr-om", and tools/harness/build.mjs's import scanner
//     matches that pair anywhere in a file, comments included — so it
//     reported an import of a module named after the tier and printed
//     "non-firebase external imports present" on every mirror. Third time
//     in one session a check read PROSE as code here. The mode names are
//     unquoted now; the scanner's own gap is handed off, because the next
//     person to write that pair in a comment will not know why it breaks.
//   · REFUSED when the brain is not ahead on Anima. Feeding the seat
//     that is already ahead is the losing trade Monkey Suite's row
//     already refuses, in the same words, one section down.
//   · REFUSED when the adversary has nothing standing. There is no swing
//     to deter, so the offer buys no time and they simply collect.
//   · Scored LOW (20). It is the cheapest thing on the good tier's old
//     lane and it is the least urgent thing on this one.
//
// ⭐ AND THE MOMENT IS SETTLED TOO (caP'n, 2026-09-15). I asked which
// end-of-turn pays it — theirs, having had their chance to swing, or
// mine. *"since its about when they don't Lyt Attack you, yes, they
// receive the Anima at the end of the turn."* The end of the turn of the
// seat that COULD have swung and did not, which is what Shatterfield
// implements, so this row is now scoring a correct engine rather than a
// broken one. Both halves of the card are landed.
//
// ⚠ THE +2 ON RUIN IS THE OTHER HALF AND IT IS ALREADY CORRECT:
// tomeResolver.applyTomeRuinClauses pays the OWNER 2 Anima when the card
// is ruined, which is what the card says and is the consolation that
// makes the offer worth making. caP'n's *"void it immediately"* is that
// line: an adversary who will not wait ruins it, and the owner is paid.
row("tome_restraining_ordeal", {
  lane: "standing", mode: "utility", steps: [],
  want: V => (V.oppStanding.length > 0 && V.anima > V.oppAnima) ? 20 : null,
  note: "RULED 2026-09-14: the +1 a Turn goes to the ADVERSARY, for not Lyt Attacking \u2014 a peace offering, not income. This row scored it as income to its owner (the engine's behaviour) until the ruling landed; it is now valued as the stalling play it is, and refused when the brain is behind on Anima or the adversary has nothing standing to deter. RULED 2026-09-15 on the moment: they are paid at the end of the turn of the seat that could have swung and did not \u2014 which is what the board now implements, so this row scores a correct engine. Both halves landed.",
});
row("tome_agree_to_disagree", {
  lane: "activated", mode: "utility", steps: [{ k: "agreeToDisagree" }],
  // The MODAL the brief called out. It is genuinely resolvable: the
  // player side already picks for the adversary with a one-line
  // heuristic (opponent under 20 Anima takes the Anima), so the NPC
  // running the same heuristic is parity, not invention.
  want: V => Object.keys(V.grim || {}).length > 0 ? 40 : null,
  note: "the adversary's choice is made by the same heuristic the player-side branch uses for the NPC (opp under 20 Anima prefers the Anima). Not a real negotiation on either side.",
});
row("tome_solomon_godfry", {
  lane: "activated", mode: "aggro", steps: [{ k: "solomon" }],
  // BOTH SEATS must ruin 1 of their own beings and its Soul.
  // ⚠ The printed text on this card says "Warlocks"; the shipping game
  // calls them AENARCHISTS everywhere else (RULEZ_V2 ch.25). Named, not
  // reproduced — see the matching note in tomeResolver's branch, and the
  // handoff for src/cards.js, which is the copy a player reads.
  // The brain fired this unconditionally and would happily eat its own best
  // being against an empty adversary board. It is only a trade when
  // there is something on the other side to trade FOR.
  want: V => {
    if (V.oppBeings.length === 0) return null;   // pure self-harm
    if (V.myBeings.length === 0) return 60;      // free kill, nothing to pay
    return 45;
  },
});
row("tome_magma_carta", {
  lane: "activated", mode: "utility",
  steps: [{ k: "selfDim", n: 4 }, { k: "drawSelf", n: 1 }],
  // R96 (THE TILL LAW) — a voluntary payment may never take you to 0.
  // Four Anima for one card is also a bad rate under pressure, so the
  // brain wants real headroom, not just legality.
  want: V => (canPayVoluntary(V.anima, 4) && V.anima >= 14 &&
              Object.keys(V.grim || {}).length > 0) ? 26 : null,
});
row("tome_seize_and_disease", {
  lane: "activated", mode: "aggro", steps: [{ k: "ruinPair" }],
  // Symmetric tome destruction. With nothing on their side it is a
  // one-sided ruin of my OWN board.
  want: V => V.oppTomes.some(t => t?.id) ? 50 : null,
});
row("tome_king_of_the_castle", {
  lane: "standing", mode: "good", steps: [],
  // The card's whole body is an obligation on the ADVERSARY ("must
  // devote at least 2 of their Anima"), collected by the adversary's
  // own client. NPC.js now honours that obligation when the PLAYER owns
  // one (npcHonourDevotions), so the contract is real in both
  // directions — but it is worth 10 Khao only when there is an
  // adversary with Anima to take.
  want: V => V.oppAnima > 2 ? 30 : null,
});

// ─────────────────────────────────────────────────────────────
// ⭐ THE EXHAUSTIVENESS GATE — THIS IS THE POINT OF THE FILE
// ─────────────────────────────────────────────────────────────
// Both directions, at module load, loudly. A Tome added to cards.js
// with no row here fails the import. A row naming an id that cards.js
// does not have fails the import. There is no "hand-maintained set
// that must grow with the dispatcher" any more, because the set cannot
// be wrong and still start.
//
// The dead id `tome_omnis_corruption_jet` sat in AGGRESSIVE_TOMES for
// months. Under this gate it survives exactly zero seconds.
// ─────────────────────────────────────────────────────────────
{
  const catalogue = new Set(TOMES.map(t => t.id));
  const book = new Set(Object.keys(R));
  const missing = [...catalogue].filter(id => !book.has(id));
  const phantom = [...book].filter(id => !catalogue.has(id));
  const problems = [];
  if (missing.length) problems.push(`${missing.length} Tome(s) in cards.js have NO row: ${missing.join(", ")}`);
  if (phantom.length) problems.push(`${phantom.length} row(s) name a Tome that does not exist: ${phantom.join(", ")}`);
  // A row that declines to resolve must SAY WHY. An undeclared refusal
  // is indistinguishable from an oversight, which is how the last gap
  // hid for so long.
  const mute = Object.values(R).filter(r => (r.lane === "human-only" || r.lane === "reactive") && !r.note);
  if (mute.length) problems.push(`${mute.length} non-resolving row(s) carry no reason: ${mute.map(r => r.id).join(", ")}`);
  const badLane = Object.values(R).filter(
    r => !["activated", "standing", "reactive", "human-only"].includes(r.lane));
  if (badLane.length) problems.push(`bad lane on: ${badLane.map(r => r.id).join(", ")}`);
  // ── `standing` IS THE LANE, AND SAYING IT TWICE IS A DEFECT ───────
  // See the banner over row(). A spec that still hand-declares
  // `standing` is either agreeing with the lane (noise) or
  // contradicting it (tome_necklace_death and tome_necklace_courage,
  // fed exactly once each for their whole shipping life). Both are
  // refused HERE, at import, so the row cannot half-exist — the same
  // treatment a Tome with no row gets.
  if (_explicitStanding.length) {
    problems.push(
      `${_explicitStanding.length} row(s) hand-declare \`standing\`, which is derived from \`lane\` and is not a second opinion: ` +
      `${_explicitStanding.join(", ")}. Say \`lane: "standing"\` (flip once, never re-charge) or \`lane: "activated"\` (the brain works it every Turn) — never both.`);
  }
  // ── AND THE SHAPE THAT MADE IT A REAL BUG ────────────────────────
  // A `feed` step is BY CONSTRUCTION charged again every Turn: it moves
  // a currency onto the card `per` at a time until it reaches `cap`,
  // and its own want() refuses a second feed in the same Turn. A row
  // whose value is "being face-up" cannot also be a row that must be
  // worked every Turn to do anything at all, and activateOneTome —
  // which tests standing FIRST — is where the contradiction becomes
  // two dead cards. So the two words may never appear together.
  const fedButStanding = Object.values(R).filter(
    r => r.lane === "standing" && (r.steps || []).some(st => st && st.k === "feed"));
  if (fedButStanding.length) {
    problems.push(
      `${fedButStanding.length} row(s) are on the STANDING lane and carry a per-Turn \`feed\` step, which activateOneTome will never reach: ` +
      `${fedButStanding.map(r => r.id).join(", ")}`);
  }
  if (problems.length) {
    throw new Error("NPC TOME BOOK IS OUT OF SYNC WITH cards.js:\n  " + problems.join("\n  "));
  }
}

export const TOME_BOOK = R;
export const TOME_BOOK_SIZE = Object.keys(R).length;

// ── THE ONE PUBLIC QUESTION ─────────────────────────────────────────
// "Is firing this Tome, right now, on this board, worth its fare?"
// Null means no, and the caller must not pay. Everything that used to
// be answered by four id lists is answered here.
export function tomeWant(id, V) {
  const r = R[baseId(id)];
  if (!r) return null;
  if (r.lane === "human-only") return null;
  try { return r.want(V); } catch (_) { return null; }
}

export const tomeRow = (id) => R[baseId(id)] || null;
export const tomeLane = (id) => R[baseId(id)]?.lane || null;
export const tomeMode = (id) => R[baseId(id)]?.mode || null;
// A standing Tome's fire step is a no-op by construction: flipping it
// bought the whole effect. Charging it again on a cooldown is the
// pay-for-nothing bug wearing a different hat.
export const tomeIsStanding = (id) => !!R[baseId(id)]?.standing;
// Placement gate. A Tome the brain can never resolve must never take
// one of three rifts hostage for the rest of the duel.
export const tomeIsPlaceable = (id) => {
  const r = R[baseId(id)];
  return !!r && r.lane !== "human-only";
};
// The reactive lane, for the counter brain and for the coverage report.
export const tomeIsReactive = (id) => {
  const r = R[baseId(id)];
  return !!r && (r.lane === "reactive" || r.alsoReactive === true);
};

// ── COVERAGE SELF-DESCRIPTION ───────────────────────────────────────
// The balancer asks the book what it CLAIMS so it can compare that
// against what actually happened in ten thousand turns. A claim this
// file cannot back up shows up as a Tome that was played and changed
// nothing — see section 2b of tools/balance.mjs.
export function bookSummary() {
  const byLane = {};
  Object.values(R).forEach(r => { byLane[r.lane] = (byLane[r.lane] || 0) + 1; });
  return {
    total: Object.keys(R).length,
    byLane,
    standing: Object.values(R).filter(r => r.standing).length,
    noted: Object.values(R).filter(r => r.note).map(r => ({ id: r.id, note: r.note })),
  };
}
